import React, { useState, useEffect } from 'react';
import { Navigate } from 'react-router-dom';
import { useAuth } from '../../api/auth/AuthContext';
import {
  getPermissions,
  createPermission,
  updatePermission,
  deletePermission,
  getPermissionUsers,
  getUserPermissions,
  PermissionDto,
  EntraUserDto,
  WeighedPermissionDto,
} from '../../api/permissions/permissions-api';
import './Permissions.css';

type ActiveTab = 'permissions' | 'users';

interface FormState {
  subjectType: string;
  subjectId: string;
  resourceName: string;
  priority: string;
  notes: string;
}

const PAGE_SIZE = 20;

function formatDate(dateStr: string | undefined): string {
  if (!dateStr) return '—';
  return new Date(dateStr).toLocaleDateString('es-MX', {
    day: '2-digit',
    month: 'short',
    year: 'numeric',
  });
}


const Permissions: React.FC = () => {
  const { roles } = useAuth();

  const [activeTab, setActiveTab] = useState<ActiveTab>('permissions');

  // — Permissions tab —
  const [permissions, setPermissions] = useState<PermissionDto[]>([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [revision, setRevision] = useState(0);
  const [loading, setLoading] = useState(false);
  const [loadError, setLoadError] = useState<string | null>(null);

  const [formOpen, setFormOpen] = useState(false);
  const [editingId, setEditingId] = useState<number | null>(null);
  const [form, setForm] = useState<FormState>({ subjectType: '', subjectId: '', resourceName: '', priority: '', notes: '' });
  const [policyText, setPolicyText] = useState('{}');
  const [policyError, setPolicyError] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState<string | null>(null);

  const [confirmDeleteId, setConfirmDeleteId] = useState<number | null>(null);
  const [deleting, setDeleting] = useState(false);
  const [deleteError, setDeleteError] = useState<string | null>(null);

  // — Users tab —
  const [users, setUsers] = useState<EntraUserDto[]>([]);
  const [usersLoaded, setUsersLoaded] = useState(false);
  const [usersLoading, setUsersLoading] = useState(false);
  const [usersError, setUsersError] = useState<string | null>(null);
  const [roleFilters, setRoleFilters] = useState<string[]>([]);
  const [groupFilters, setGroupFilters] = useState<string[]>([]);
  const [selectedUser, setSelectedUser] = useState<EntraUserDto | null>(null);
  const [showAllGroups, setShowAllGroups] = useState(false);
  const [gruposRolesOpen, setGruposRolesOpen] = useState(false);

  // — User permission lookup —
  const [userPermissionResource, setUserPermissionResource] = useState('');
  const [userPermissions, setUserPermissions] = useState<WeighedPermissionDto[]>([]);
  const [userPermissionsLoading, setUserPermissionsLoading] = useState(false);
  const [userPermissionsError, setUserPermissionsError] = useState<string | null>(null);
  const [userPermissionsLoaded, setUserPermissionsLoaded] = useState(false);

  const totalPages = Math.max(1, Math.ceil(total / PAGE_SIZE));
  const refresh = () => setRevision(r => r + 1);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setLoadError(null);
    getPermissions(page, PAGE_SIZE, {
      onSuccess: (data) => {
        if (!cancelled) {
          setPermissions(data.data ?? []);
          setTotal(Number(data.total ?? 0));
          setLoading(false);
        }
      },
      onError: (msg) => {
        if (!cancelled) {
          setLoadError(msg);
          setLoading(false);
        }
      },
    });
    return () => { cancelled = true; };
  }, [page, revision]);

  useEffect(() => {
    setUserPermissionResource('');
    setUserPermissions([]);
    setUserPermissionsLoading(false);
    setUserPermissionsError(null);
    setUserPermissionsLoaded(false);
  }, [selectedUser]);

  if (!roles.includes('Admin')) {
    return <Navigate to="/admin" replace />;
  }

  // Derived — unique roles & groups across all users
  const allRoles = Array.from(new Set(users.flatMap(u => u.roles ?? []))).sort();
  const allGroups = Array.from(new Set(users.flatMap(u => u.memberships ?? []))).sort();
  const PORTAL_GROUP_PREFIX = 'AAD-GRP-Portal-';
  const displayedGroups = showAllGroups
    ? allGroups
    : allGroups.filter(g => g.startsWith(PORTAL_GROUP_PREFIX));

  // Derived — filtered users (OR within each type, AND across types)
  const filteredUsers = users.filter(u => {
    if (roleFilters.length > 0 && !roleFilters.some(r => u.roles?.includes(r))) return false;
    if (groupFilters.length > 0 && !groupFilters.some(g => u.memberships?.includes(g))) return false;
    return true;
  });

  const hasFilters = roleFilters.length > 0 || groupFilters.length > 0;

  // — Permissions handlers —
  const openCreate = () => {
    setEditingId(null);
    setForm({ subjectType: '', subjectId: '', resourceName: '', priority: '', notes: '' });
    setPolicyText('{}');
    setPolicyError(null);
    setSaveError(null);
    setFormOpen(true);
  };

  const openEdit = (p: PermissionDto) => {
    setEditingId(Number(p.id));
    setForm({
      subjectType: p.subjectType ?? '',
      subjectId: p.subjectId ?? '',
      resourceName: p.resourceName ?? '',
      priority: p.priority != null ? String(p.priority) : '',
      notes: p.notes ?? '',
    });
    let display = p.policy ?? '{}';
    try { display = JSON.stringify(JSON.parse(display), null, 2); } catch { /* keep raw */ }
    setPolicyText(display);
    setPolicyError(null);
    setSaveError(null);
    setFormOpen(true);
  };

  const closeForm = () => {
    setFormOpen(false);
    setEditingId(null);
  };

  const handlePolicyChange = (value: string) => {
    setPolicyText(value);
    if (policyError) {
      try { JSON.parse(value); setPolicyError(null); } catch { /* keep error */ }
    }
  };

  const handlePolicyBlur = () => {
    if (!policyText.trim()) { setPolicyError(null); return; }
    try {
      JSON.parse(policyText);
      setPolicyError(null);
    } catch {
      setPolicyError('JSON inválido');
    }
  };

  const handleFormatPolicy = () => {
    try {
      setPolicyText(JSON.stringify(JSON.parse(policyText), null, 2));
      setPolicyError(null);
    } catch {
      setPolicyError('JSON inválido — no se puede formatear');
    }
  };

  const handlePolicyKeyDown = (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
    if (e.key === 'Tab') {
      e.preventDefault();
      const ta = e.currentTarget;
      const start = ta.selectionStart;
      const end = ta.selectionEnd;
      const updated = policyText.substring(0, start) + '  ' + policyText.substring(end);
      setPolicyText(updated);
      setTimeout(() => { ta.selectionStart = ta.selectionEnd = start + 2; }, 0);
    }
  };

  const handleSave = async () => {
    let policyValue: string | null = null;
    if (policyText.trim()) {
      try {
        JSON.parse(policyText);
        policyValue = policyText;
      } catch {
        setPolicyError('JSON inválido');
        return;
      }
    }

    if (!form.subjectType.trim() || !form.subjectId.trim() || !form.resourceName.trim()) {
      setSaveError('Tipo de sujeto, ID de sujeto y nombre de recurso son obligatorios.');
      return;
    }

    const priorityRaw = form.priority.trim();
    if (priorityRaw && (isNaN(Number(priorityRaw)) || !Number.isInteger(Number(priorityRaw)))) {
      setSaveError('Peso debe ser un número entero.');
      return;
    }

    const dto: PermissionDto = {
      ...(editingId !== null ? { id: editingId } : {}),
      subjectType: form.subjectType.trim(),
      subjectId: form.subjectId.trim(),
      resourceName: form.resourceName.trim(),
      policy: policyValue,
      priority: priorityRaw ? Number(priorityRaw) : null,
      notes: form.notes.trim() || null,
    };

    setSaving(true);
    setSaveError(null);

    const onError = (msg: string) => { setSaveError(msg); setSaving(false); };
    const onBadRequest = (msg: string) => { setSaveError(msg || 'Datos inválidos'); setSaving(false); };

    if (editingId !== null) {
      await updatePermission(editingId, dto, {
        onSuccess: () => { setSaving(false); closeForm(); refresh(); },
        onError,
        onBadRequest,
      });
    } else {
      await createPermission(dto, {
        onSuccess: () => { setSaving(false); closeForm(); refresh(); },
        onError,
        onBadRequest,
      });
    }
  };

  const handleDeleteConfirm = async () => {
    if (confirmDeleteId === null) return;
    setDeleting(true);
    setDeleteError(null);
    await deletePermission(confirmDeleteId, {
      onSuccess: () => {
        setDeleting(false);
        setConfirmDeleteId(null);
        refresh();
      },
      onError: (msg) => { setDeleteError(msg); setDeleting(false); },
    });
  };

  // — Users handlers —
  const handleLoadUsers = () => {
    setUsersLoading(true);
    setUsersError(null);
    getPermissionUsers({
      onSuccess: (data) => {
        setUsers(data);
        setUsersLoaded(true);
        setUsersLoading(false);
      },
      onError: (msg) => {
        setUsersError(msg);
        setUsersLoading(false);
      },
    });
  };

  const toggleRoleFilter = (role: string) => {
    setRoleFilters(prev => prev.includes(role) ? prev.filter(r => r !== role) : [...prev, role]);
    setSelectedUser(null);
  };

  const toggleGroupFilter = (group: string) => {
    setGroupFilters(prev => prev.includes(group) ? prev.filter(g => g !== group) : [...prev, group]);
    setSelectedUser(null);
  };

  const clearFilters = () => {
    setRoleFilters([]);
    setGroupFilters([]);
  };

  const handleLookupUserPermissions = () => {
    if (!selectedUser?.email || !userPermissionResource.trim()) return;
    setUserPermissionsLoading(true);
    setUserPermissionsError(null);
    setUserPermissionsLoaded(false);
    getUserPermissions(selectedUser.email, userPermissionResource.trim(), {
      onSuccess: (data) => {
        setUserPermissions(data);
        setUserPermissionsLoaded(true);
        setUserPermissionsLoading(false);
      },
      onError: (msg) => {
        setUserPermissionsError(msg);
        setUserPermissionsLoading(false);
      },
    });
  };

  return (
    <div className="pm-root">
      <div className="pm-header">
        <h1 className="pm-title">
          {activeTab === 'permissions' ? 'Permisos de Usuario' : 'Directorio de Usuarios'}
        </h1>
        {activeTab === 'permissions' && (
          <button className="pm-new-btn" onClick={openCreate}>+ Nuevo Permiso</button>
        )}
        {activeTab === 'users' && usersLoaded && (
          <button className="pm-new-btn" onClick={handleLoadUsers} disabled={usersLoading}>
            {usersLoading ? 'Actualizando…' : '↻ Actualizar'}
          </button>
        )}
      </div>

      {/* Tabs */}
      <div className="pm-tabs">
        <button
          className={`pm-tab${activeTab === 'permissions' ? ' pm-tab--active' : ''}`}
          onClick={() => setActiveTab('permissions')}
        >
          Permisos
        </button>
        <button
          className={`pm-tab${activeTab === 'users' ? ' pm-tab--active' : ''}`}
          onClick={() => setActiveTab('users')}
        >
          Usuarios
        </button>
      </div>

      {/* ── Permissions Tab ─────────────────────────────── */}
      {activeTab === 'permissions' && (
        <>
          {formOpen && (
            <div className="pm-form-panel">
              <div className="pm-form-header">
                <span className="pm-form-title">
                  {editingId !== null ? `Editar Permiso #${editingId}` : 'Nuevo Permiso'}
                </span>
                <button className="pm-form-close" onClick={closeForm} aria-label="Cerrar">✕</button>
              </div>

              <div className="pm-form-fields">
                <div className="pm-field">
                  <label className="pm-field-label">Tipo de Sujeto</label>
                  <select
                    className="pm-field-input pm-field-select"
                    value={form.subjectType}
                    onChange={e => setForm(f => ({ ...f, subjectType: e.target.value }))}
                  >
                    <option value="">— Seleccionar —</option>
                    <option value="User">User</option>
                    <option value="Role">Role</option>
                    <option value="Group">Group</option>
                  </select>
                </div>
                <div className="pm-field">
                  <label className="pm-field-label">ID de Sujeto</label>
                  <input
                    className="pm-field-input"
                    value={form.subjectId}
                    onChange={e => setForm(f => ({ ...f, subjectId: e.target.value }))}
                    placeholder="ID del usuario, grupo o rol"
                  />
                </div>
                <div className="pm-field">
                  <label className="pm-field-label">Nombre de Recurso</label>
                  <input
                    className="pm-field-input"
                    value={form.resourceName}
                    onChange={e => setForm(f => ({ ...f, resourceName: e.target.value }))}
                    placeholder="Recurso o sección del portal"
                  />
                </div>
                <div className="pm-field pm-field pm-field--span3">
                  <label className="pm-field-label">Peso</label>
                  <p className="pm-policy-hint">Asigne un peso para subir la prioridad. Cuando el usuario pertenece a múltiples grupos (10) o roles (1), el peso ayuda a decidir cuál tiene mayor prioridad.</p>
                  <input
                    className="pm-field-input"
                    type="number"
                    min="0"
                    step="1"
                    value={form.priority}
                    onChange={e => setForm(f => ({ ...f, priority: e.target.value }))}
                    placeholder="Ej. 10"
                  />
                </div>
                <div className="pm-field pm-field--span3">
                  <label className="pm-field-label">Notas</label>
                  <input
                    className="pm-field-input"
                    value={form.notes}
                    onChange={e => setForm(f => ({ ...f, notes: e.target.value }))}
                    placeholder="Observaciones opcionales"
                  />
                </div>
              </div>

              <div className="pm-policy-section">
                <div className="pm-policy-header">
                  <div>
                    <label className="pm-field-label">Política (JSON)</label>
                    <p className="pm-policy-hint">
                      Objeto JSON con las reglas de acceso. Ejemplo: {`{"canView": true, "canEdit": false}`}
                    </p>
                  </div>
                  <button className="pm-format-btn" onClick={handleFormatPolicy} type="button">
                    Formatear JSON
                  </button>
                </div>
                <textarea
                  className={`pm-policy-editor${policyError ? ' pm-policy-editor--error' : ''}`}
                  value={policyText}
                  onChange={e => handlePolicyChange(e.target.value)}
                  onBlur={handlePolicyBlur}
                  onKeyDown={handlePolicyKeyDown}
                  spellCheck={false}
                  rows={10}
                  aria-label="Política en formato JSON"
                />
                {policyError && <p className="pm-policy-error">{policyError}</p>}
              </div>

              {saveError && <p className="pm-save-error">{saveError}</p>}

              <div className="pm-form-actions">
                <button className="pm-cancel-btn" onClick={closeForm} disabled={saving}>
                  Cancelar
                </button>
                <button className="pm-save-btn" onClick={handleSave} disabled={saving}>
                  {saving
                    ? 'Guardando…'
                    : editingId !== null ? 'Guardar cambios' : 'Crear permiso'}
                </button>
              </div>
            </div>
          )}

          {confirmDeleteId !== null && (
            <div className="pm-delete-confirm">
              <span className="pm-delete-text">
                ¿Eliminar permiso <strong>#{confirmDeleteId}</strong>? Esta acción no se puede deshacer.
              </span>
              {deleteError && <span className="pm-delete-error">{deleteError}</span>}
              <div className="pm-delete-actions">
                <button
                  className="pm-cancel-btn"
                  onClick={() => { setConfirmDeleteId(null); setDeleteError(null); }}
                  disabled={deleting}
                >
                  Cancelar
                </button>
                <button className="pm-delete-btn" onClick={handleDeleteConfirm} disabled={deleting}>
                  {deleting ? 'Eliminando…' : 'Eliminar'}
                </button>
              </div>
            </div>
          )}

          {loading && <p className="pm-loading">Cargando permisos…</p>}
          {loadError && <p className="pm-load-error">{loadError}</p>}

          {!loading && !loadError && (
            <>
              <div className="pm-table-wrap">
                <table className="pm-table">
                  <thead>
                    <tr>
                      <th>ID</th>
                      <th>Tipo</th>
                      <th>Sujeto</th>
                      <th>Recurso</th>
                      <th>Peso</th>
                      <th>Notas</th>
                      <th>Actualizado</th>
                      <th></th>
                    </tr>
                  </thead>
                  <tbody>
                    {permissions.length === 0 ? (
                      <tr>
                        <td colSpan={8} className="pm-empty">
                          No hay permisos registrados
                        </td>
                      </tr>
                    ) : (
                      permissions.map(p => (
                        <tr
                          key={String(p.id)}
                          className={editingId === Number(p.id) ? 'pm-row--editing' : ''}
                        >
                          <td className="pm-cell-id">#{String(p.id ?? '—')}</td>
                          <td>
                            {p.subjectType ? (
                              <span className="pm-subject-type-badge">{p.subjectType}</span>
                            ) : '—'}
                          </td>
                          <td className="pm-cell-subject" title={p.subjectId ?? ''}>
                            {p.subjectId ?? '—'}
                          </td>
                          <td className="pm-cell-resource" title={p.resourceName ?? ''}>
                            {p.resourceName ?? '—'}
                          </td>
                          <td className="pm-cell-priority">
                            {p.priority != null ? Number(p.priority) : '—'}
                          </td>
                          <td className="pm-cell-notes" title={p.notes ?? ''}>
                            {p.notes
                              ? <span className="pm-notes-text">{p.notes}</span>
                              : <span className="pm-no-policy">—</span>}
                          </td>
                          <td className="pm-cell-date">
                            <span>{formatDate(p.updatedAt)}</span>
                            {p.updatedBy && (
                              <span className="pm-updated-by" title={p.updatedBy}>
                                {p.updatedBy}
                              </span>
                            )}
                          </td>
                          <td className="pm-cell-actions">
                            <button
                              className="pm-icon-btn"
                              onClick={() => openEdit(p)}
                              title="Editar"
                              aria-label={`Editar permiso #${p.id}`}
                            >
                              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7" />
                                <path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z" />
                              </svg>
                            </button>
                            <button
                              className="pm-icon-btn pm-icon-btn--delete"
                              onClick={() => { setConfirmDeleteId(Number(p.id)); setDeleteError(null); }}
                              title="Eliminar"
                              aria-label={`Eliminar permiso #${p.id}`}
                            >
                              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                <polyline points="3 6 5 6 21 6" />
                                <path d="M19 6l-1 14a2 2 0 0 1-2 2H8a2 2 0 0 1-2-2L5 6" />
                                <path d="M10 11v6" /><path d="M14 11v6" />
                                <path d="M9 6V4a1 1 0 0 1 1-1h4a1 1 0 0 1 1 1v2" />
                              </svg>
                            </button>
                          </td>
                        </tr>
                      ))
                    )}
                  </tbody>
                </table>
              </div>

              {totalPages > 1 && (
                <div className="pm-pagination">
                  <span className="pm-pagination-info">
                    {((page - 1) * PAGE_SIZE) + 1}–{Math.min(page * PAGE_SIZE, total)} de {total}
                  </span>
                  <button
                    className="pm-page-btn"
                    onClick={() => setPage(p => Math.max(1, p - 1))}
                    disabled={page <= 1}
                  >
                    ‹ Anterior
                  </button>
                  <span className="pm-page-indicator">{page} / {totalPages}</span>
                  <button
                    className="pm-page-btn"
                    onClick={() => setPage(p => Math.min(totalPages, p + 1))}
                    disabled={page >= totalPages}
                  >
                    Siguiente ›
                  </button>
                </div>
              )}
            </>
          )}
        </>
      )}

      {/* ── Users Tab ────────────────────────────────────── */}
      {activeTab === 'users' && (
        <div className="pm-users-root">
          {!usersLoaded && !usersLoading && (
            <div className="pm-users-cta">
              <p className="pm-users-cta-text">
                Consulte el directorio de usuarios de Entra ID con sus roles y grupos asignados.
              </p>
              <button className="pm-load-users-btn" onClick={handleLoadUsers}>
                Cargar directorio
              </button>
            </div>
          )}

          {usersLoading && <p className="pm-loading">Cargando usuarios…</p>}
          {usersError && <p className="pm-load-error">{usersError}</p>}

          {usersLoaded && !usersLoading && (
            <>
              {/* Filter panel */}
              {(allRoles.length > 0 || allGroups.length > 0) && (
                <div className="pm-filter-panel">
                  <div className="pm-filter-panel-header">
                    <span className="pm-field-label">Filtros</span>
                    {hasFilters && (
                      <button className="pm-user-filter-clear" onClick={clearFilters}>
                        × Limpiar todo
                      </button>
                    )}
                  </div>

                  {allRoles.length > 0 && (
                    <div className="pm-filter-row">
                      <span className="pm-filter-row-label">Roles</span>
                      <div className="pm-badge-list">
                        {allRoles.map(r => (
                          <button
                            key={r}
                            className={`pm-role-badge pm-badge-btn${roleFilters.includes(r) ? ' pm-badge-btn--active' : ''}`}
                            onClick={() => toggleRoleFilter(r)}
                          >
                            {r}
                          </button>
                        ))}
                      </div>
                    </div>
                  )}

                  {allGroups.length > 0 && (
                    <div className="pm-filter-row">
                      <div className="pm-filter-row-label-col">
                        <span className="pm-filter-row-label">Grupos</span>
                        <label className="pm-show-all-groups-label">
                          <input
                            type="checkbox"
                            checked={showAllGroups}
                            onChange={e => setShowAllGroups(e.target.checked)}
                          />
                          Todos
                        </label>
                      </div>
                      <div className="pm-badge-list">
                        {displayedGroups.length === 0
                          ? <span className="pm-no-policy">Sin grupos de portal</span>
                          : displayedGroups.map(g => (
                              <button
                                key={g}
                                className={`pm-group-badge pm-badge-btn${groupFilters.includes(g) ? ' pm-badge-btn--active' : ''}`}
                                onClick={() => toggleGroupFilter(g)}
                              >
                                {g}
                              </button>
                            ))
                        }
                      </div>
                    </div>
                  )}
                </div>
              )}

              {/* User detail panel */}
              {selectedUser && (
                <div className="pm-user-detail">
                  <div className="pm-user-detail-header">
                    <div className="pm-user-detail-identity">
                      <span className="pm-user-detail-name">{selectedUser.name ?? '—'}</span>
                      <span className="pm-user-detail-email">{selectedUser.email ?? '—'}</span>
                    </div>
                    <button
                      className="pm-form-close"
                      onClick={() => setSelectedUser(null)}
                      aria-label="Cerrar detalle"
                    >
                      ✕
                    </button>
                  </div>
                  <div className="pm-user-detail-body">
                    <div className="pm-user-detail-section">
                      <span className="pm-field-label">
                        Roles ({selectedUser.roles?.length ?? 0})
                      </span>
                      <div className="pm-badge-list">
                        {(selectedUser.roles ?? []).length > 0
                          ? (selectedUser.roles ?? []).map(r => (
                              <button
                                key={r}
                                className={`pm-role-badge pm-badge-btn${roleFilters.includes(r) ? ' pm-badge-btn--active' : ''}`}
                                onClick={() => toggleRoleFilter(r)}
                                title={roleFilters.includes(r) ? `Quitar filtro: ${r}` : `Filtrar por rol: ${r}`}
                              >
                                {r}
                              </button>
                            ))
                          : <span className="pm-no-policy">Sin roles asignados</span>
                        }
                      </div>
                    </div>
                    <div className="pm-user-detail-section">
                      <span className="pm-field-label">
                        Grupos ({selectedUser.memberships?.length ?? 0})
                      </span>
                      <div className="pm-badge-list">
                        {(() => {
                          const allMemberships = selectedUser.memberships ?? [];
                          const visible = showAllGroups
                            ? allMemberships
                            : allMemberships.filter(g => g.startsWith(PORTAL_GROUP_PREFIX));
                          if (allMemberships.length === 0) return <span className="pm-no-policy">Sin grupos asignados</span>;
                          if (visible.length === 0) return <span className="pm-no-policy">Sin grupos de portal asignados</span>;
                          return visible.map(g => (
                            <button
                              key={g}
                              className={`pm-group-badge pm-badge-btn${groupFilters.includes(g) ? ' pm-badge-btn--active' : ''}`}
                              onClick={() => toggleGroupFilter(g)}
                              title={groupFilters.includes(g) ? `Quitar filtro: ${g}` : `Filtrar por grupo: ${g}`}
                            >
                              {g}
                            </button>
                          ));
                        })()}
                      </div>
                    </div>
                  </div>

                  <div className="pm-user-perm-lookup">
                    <span className="pm-field-label">Permisos Efectivos por Recurso</span>
                    <div className="pm-user-perm-lookup-row">
                      <input
                        className="pm-field-input pm-user-perm-resource-input"
                        value={userPermissionResource}
                        onChange={e => setUserPermissionResource(e.target.value)}
                        onKeyDown={e => { if (e.key === 'Enter') handleLookupUserPermissions(); }}
                        placeholder="Nombre de recurso (ej. Sku)"
                      />
                      <button
                        className="pm-load-users-btn pm-user-perm-search-btn"
                        onClick={handleLookupUserPermissions}
                        disabled={userPermissionsLoading || !userPermissionResource.trim()}
                      >
                        {userPermissionsLoading ? 'Buscando…' : 'Buscar'}
                      </button>
                    </div>
                    {userPermissionsError && <p className="pm-load-error">{userPermissionsError}</p>}
                    {userPermissionsLoaded && !userPermissionsLoading && (
                      userPermissions.length === 0
                        ? <p className="pm-no-policy pm-user-perm-empty">Sin permisos para este recurso</p>
                        : <div className="pm-timeline">
                            {userPermissions.map((wp, i) => (
                              <div key={i} className={`pm-timeline-node${i === userPermissions.length - 1 ? ' pm-timeline-node--active' : ''}`}>
                                <div className="pm-timeline-track">
                                  <div className="pm-timeline-dot" />
                                  {i < userPermissions.length - 1 && <div className="pm-timeline-connector" />}
                                </div>
                                <div className="pm-timeline-content">
                                  <div className="pm-timeline-row1">
                                    <span className="pm-timeline-weight-badge">
                                      {wp.weight != null ? String(Number(wp.weight)) : '—'}
                                    </span>
                                    <span className="pm-timeline-perm-id">#{String(wp.permission?.id ?? '—')}</span>
                                    {wp.permission?.subjectType && (
                                      <span className="pm-subject-type-badge">{wp.permission.subjectType}</span>
                                    )}
                                    <span className="pm-timeline-subject-id">{wp.permission?.subjectId ?? '—'}</span>
                                    {wp.permission?.priority != null && (
                                      <span className="pm-timeline-priority">({Number(wp.permission.priority)})</span>
                                    )}
                                  </div>
                                  {wp.permission?.notes && (
                                    <div className="pm-timeline-notes">{wp.permission.notes}</div>
                                  )}
                                </div>
                              </div>
                            ))}
                          </div>
                    )}
                  </div>
                </div>
              )}

              {/* Users table */}
              <div className="pm-table-wrap">
                <table className="pm-table">
                  <thead>
                    <tr>
                      <th>Nombre</th>
                      <th>Email</th>
                      <th>Roles</th>
                      <th>Grupos</th>
                    </tr>
                  </thead>
                  <tbody>
                    {filteredUsers.length === 0 ? (
                      <tr>
                        <td colSpan={4} className="pm-empty">
                          {hasFilters
                            ? 'Ningún usuario coincide con los filtros seleccionados'
                            : 'No se encontraron usuarios'}
                        </td>
                      </tr>
                    ) : (
                      filteredUsers.map((u, i) => (
                        <tr
                          key={u.email ?? i}
                          className={`pm-user-row${selectedUser?.email === u.email ? ' pm-row--editing' : ''}`}
                          onClick={() => setSelectedUser(prev => prev?.email === u.email ? null : u)}
                        >
                          <td className="pm-user-name">{u.name ?? '—'}</td>
                          <td className="pm-user-email">{u.email ?? '—'}</td>
                          <td>
                            <div className="pm-badge-list">
                              {(u.roles ?? []).map(r => (
                                <button
                                  key={r}
                                  className={`pm-role-badge pm-badge-btn${roleFilters.includes(r) ? ' pm-badge-btn--active' : ''}`}
                                  onClick={e => { e.stopPropagation(); toggleRoleFilter(r); }}
                                  title={roleFilters.includes(r) ? `Quitar filtro: ${r}` : `Filtrar por rol: ${r}`}
                                >
                                  {r}
                                </button>
                              ))}
                            </div>
                          </td>
                          <td>
                            <div className="pm-badge-list">
                              {(showAllGroups
                                ? (u.memberships ?? [])
                                : (u.memberships ?? []).filter(g => g.startsWith(PORTAL_GROUP_PREFIX))
                              ).map(g => (
                                <button
                                  key={g}
                                  className={`pm-group-badge pm-badge-btn${groupFilters.includes(g) ? ' pm-badge-btn--active' : ''}`}
                                  onClick={e => { e.stopPropagation(); toggleGroupFilter(g); }}
                                  title={groupFilters.includes(g) ? `Quitar filtro: ${g}` : `Filtrar por grupo: ${g}`}
                                >
                                  {g}
                                </button>
                              ))}
                            </div>
                          </td>
                        </tr>
                      ))
                    )}
                  </tbody>
                </table>
              </div>

              <p className="pm-users-count">
                {hasFilters
                  ? `${filteredUsers.length} de ${users.length} usuarios`
                  : `${users.length} usuario${users.length !== 1 ? 's' : ''} en el directorio`}
              </p>

              <div className="pm-info-panel">
                <button
                  className="pm-info-panel-header"
                  onClick={() => setGruposRolesOpen(o => !o)}
                  aria-expanded={gruposRolesOpen}
                >
                  <span className="pm-field-label pm-info-panel-label">Grupos y roles</span>
                  <svg
                    width="16" height="16" viewBox="0 0 24 24"
                    fill="none" stroke="currentColor" strokeWidth="2.5"
                    className={gruposRolesOpen ? 'pm-chevron pm-chevron--open' : 'pm-chevron'}
                    aria-hidden="true"
                  >
                    <polyline points="6 9 12 15 18 9" />
                  </svg>
                </button>
                {gruposRolesOpen && (
                  <div className="pm-info-panel-body">
                    <div className="pm-info-links">
                      <a
                        href="https://portal.azure.com/#view/Microsoft_AAD_RegisteredApps/ApplicationMenuBlade/~/AppRoles/appId/e5f83d20-642c-4413-a630-cc587b1bdeb5"
                        target="_blank"
                        rel="noopener noreferrer"
                        className="pm-info-link"
                      >
                        <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden="true">
                          <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z"/>
                        </svg>
                        Roles — Azure App Registration
                        <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" className="pm-info-link-ext" aria-hidden="true">
                          <path d="M18 13v6a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h6"/>
                          <polyline points="15 3 21 3 21 9"/>
                          <line x1="10" y1="14" x2="21" y2="3"/>
                        </svg>
                      </a>
                      <a
                        href="https://portal.azure.com/#view/Microsoft_AAD_IAM/ManagedAppMenuBlade/~/Users/objectId/3a817256-ac9a-4f76-9a74-28ff3aa739aa/appId/e5f83d20-642c-4413-a630-cc587b1bdeb5"
                        target="_blank"
                        rel="noopener noreferrer"
                        className="pm-info-link"
                      >
                        <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden="true">
                          <path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2"/>
                          <circle cx="9" cy="7" r="4"/>
                          <path d="M23 21v-2a4 4 0 0 0-3-3.87"/>
                          <path d="M16 3.13a4 4 0 0 1 0 7.75"/>
                        </svg>
                        Asignar Roles a Usuarios — Microsoft Entra ID
                        <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" className="pm-info-link-ext" aria-hidden="true">
                          <path d="M18 13v6a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h6"/>
                          <polyline points="15 3 21 3 21 9"/>
                          <line x1="10" y1="14" x2="21" y2="3"/>
                        </svg>
                      </a>
                      <a
                        href="https://entra.microsoft.com/#view/Microsoft_AAD_IAM/GroupsManagementMenuBlade/~/AllGroups/menuId/Overview"
                        target="_blank"
                        rel="noopener noreferrer"
                        className="pm-info-link"
                      >
                        <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden="true">
                          <path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2"/>
                          <circle cx="9" cy="7" r="4"/>
                          <path d="M23 21v-2a4 4 0 0 0-3-3.87"/>
                          <path d="M16 3.13a4 4 0 0 1 0 7.75"/>
                        </svg>
                        Grupos — Microsoft Entra ID
                        <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" className="pm-info-link-ext" aria-hidden="true">
                          <path d="M18 13v6a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h6"/>
                          <polyline points="15 3 21 3 21 9"/>
                          <line x1="10" y1="14" x2="21" y2="3"/>
                        </svg>
                      </a>
                    </div>
                    <div className="pm-info-disclaimer">
                      <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" className="pm-info-disclaimer-icon" aria-hidden="true">
                        <path d="M10.29 3.86L1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z"/>
                        <line x1="12" y1="9" x2="12" y2="13"/>
                        <line x1="12" y1="17" x2="12.01" y2="17"/>
                      </svg>
                      <p>
                        Modificar grupos y roles puede accidentalmente asignar o quitar accesos privilegiados a usuarios.
                        Puede incluso afectar correos electrónicos, SharePoint y otros servicios clave de la empresa.
                        Consulte con su departamento de administración para realizar estas operaciones.
                      </p>
                    </div>
                  </div>
                )}
              </div>
            </>
          )}
        </div>
      )}
    </div>
  );
};

export default Permissions;
